import axios from 'axios'
import Router from 'next/router'

import constants from 'nexchange-service/constants'
import { resolveRequest, TAsyncRequestResult, TRequestResult } from 'utility-tools/module/requests'

import { getRefreshToken } from '../auth'
import { removeAuthenticationClientSide, saveAuthentication } from '../cookiesManagement'
import { getFiscalLocation, setFiscalLocation } from '../fiscalLocation'
import { setHeadersFromStore } from '../setHeadersFromStore'
import { setUserInfoInStorage, updateUserInfoInStorage } from '../userInfo'
import {
    IUserInfoDto,
    TAuthRespApiDto,
    TUserCreationDto,
    TUserDataApiDto,
    TUserEditDto,
} from './api.types'

const { API_URL } = constants

let refreshingToken = false

const redirect = () => {
    removeAuthenticationClientSide()
    if (window.location.pathname !== '/user/login') {
        window.location.href = '/user/login'
    }
}

export const setRefreshToken = () => {
    axios.interceptors.response.use(
        response => response,
        async error => {
            if (!refreshingToken && error.response && error.response.status === 401) {
                refreshingToken = true
                const refreshToken: string = getRefreshToken()
                if (!refreshToken) {
                    redirect()
                }
                try {
                    const response = await regenerateToken(refreshToken)
                    if (response.status !== 200) {
                        redirect()
                    }
                    refreshingToken = false
                    const originalRequest = error.config
                    originalRequest.headers.Authorization = `Bearer ${response.data.access_token}`
                    return axios(originalRequest)
                } catch (refreshError) {
                    redirect()
                }
            }
            return Promise.reject(error)
        },
    )
}

const requestIsUserRegistered = async (): Promise<boolean> => {
    setHeadersFromStore()

    return axios
        .get(`${API_URL}/users/me/`)
        .then(({ data }) => {
            const { email, phone } = data as TUserDataApiDto
            return Boolean(email || phone)
        })
        .catch(error => {
            const { response } = error
            if (!response) {
                console.error(error)
                throw error
            }

            const { status } = response
            if (status < 500) return false

            const errorMessage = 'Server Error'
            console.error(errorMessage)
            throw new Error(errorMessage)
        })
}

const requestUserInfo = async (): TAsyncRequestResult<IUserInfoDto> => {
    setHeadersFromStore()

    const response = await resolveRequest<TUserDataApiDto>(axios.get(`${API_URL}/users/me/`))
    const { data, error } = response

    if (error) {
        removeAuthenticationClientSide()
        return { data: null, error }
    }

    const { phone: phoneNumber, username, country, ...responseData } = data

    let fiscalLocation: any = getFiscalLocation(username)
    if (!fiscalLocation && country) {
        fiscalLocation = { country: { name: country } }
        setFiscalLocation(username, fiscalLocation)
    }

    const userInfoDto = <IUserInfoDto>{
        username,
        phoneNumber,
        fiscalLocation: getFiscalLocation(username),
        ...responseData,
    }

    setUserInfoInStorage(userInfoDto)

    return {
        data: userInfoDto,
        error,
    }
}

const regenerateToken = async refreshToken => {
    const formData = new FormData()

    formData.append('grant_type', 'refresh_token')
    formData.append('client_id', process.env.AUTH_CLIENT_ID)
    formData.append('refresh_token', refreshToken)

    return axios.post(`${API_URL}/oAuth2/token/`, formData, {
        headers: {
            'Content-Type': 'application/x-www-form-urlencoded;charset=UTF-8',
        },
        auth: {
            username: process.env.NEXT_PUBLIC_AUTH_CLIENT_ID,
            password: process.env.NEXT_PUBLIC_AUTH_CLIENT_SECRET,
        },
    })
}

const handleLogging = async (userInfoResp: TRequestResult<IUserInfoDto>) => {
    const { data: userInfoDto, error: userInfoError } = userInfoResp

    // DO NOT add await! Or it is going to await and skip the redirect step!
    // TODO: use it on /login page load, not in nexchange-modules
    if (userInfoError) {
        await removeAuthenticationClientSide()
        Router.push(`/404`)
    }
    // } else if (isUserMissingRequiredData(userInfoDto)) {
    //     Router.push('/user/edit?forced=true')
    // } else if (!getIsFiscalLocationLegal(userInfoDto.fiscalLocation)) {
    //     await removeAuthenticationClientSide()
    //     Router.push(`/error/${EErrorPageType.NOT_SERVICED_COUNTRY}`)
    // }
}

const requestLogIn = async (
    username: string,
    password: string,
    keepSignedIn = true,
    captchaToken: string = 'token',
): TAsyncRequestResult<TAuthRespApiDto> => {
    await removeAuthenticationClientSide()

    const formData = new FormData()
    formData.append('grant_type', 'password')
    formData.append('client_id', process.env.AUTH_CLIENT_ID)
    formData.append('username', username)
    formData.append('password', password)
    formData.append('g-recaptcha-response', captchaToken)

    const response = await resolveRequest<TAuthRespApiDto>(
        axios.post(`${API_URL}/oAuth2/token/`, formData, {
            headers: {
                'Content-Type': 'application/x-www-form-urlencoded;charset=UTF-8',
            },
            auth: {
                username: process.env.NEXT_PUBLIC_AUTH_CLIENT_ID,
                password: process.env.NEXT_PUBLIC_AUTH_CLIENT_SECRET,
            },
        }),
    )

    if (!response.error) {
        saveAuthentication({ username, ...response.data }, keepSignedIn)
        const userInfoDto = await requestUserInfo()
        await handleLogging(userInfoDto)
    }

    return response
}

const requestCreateNewUser = async ({
    username,
    email,
    phoneNumber,
    password,
    fiscalLocation,
    captchaToken,
}: TUserCreationDto) => {
    const body = {
        username,
        email,
        password,
        captchaToken,
    }

    if (phoneNumber) {
        // @ts-ignore
        body.phone = phoneNumber
    }

    if (fiscalLocation.country) {
        // @ts-ignore
        body.country = fiscalLocation.country.name
    }

    return resolveRequest(
        axios.post(`${API_URL}/users/`, body).then(response => {
            setFiscalLocation(username, fiscalLocation)
            return response
        }),
    )
}

const requestUpdateUserData = async ({ username, phoneNumber, fiscalLocation }: TUserEditDto) => {
    setFiscalLocation(username, fiscalLocation)
    updateUserInfoInStorage({ phoneNumber, fiscalLocation })

    setHeadersFromStore()

    const data = {}
    const country = fiscalLocation.country.name
    if (phoneNumber) data['phone'] = phoneNumber
    if (country) data['country'] = country

    if (Object.keys(data).length == 0) return null

    return axios.put(`${API_URL}/users/me/`, data)
}

const logOut = async (redirectUrl: string = '/user/login') => {
    removeAuthenticationClientSide()
    Router.push(redirectUrl)
}

const requestEmailResetLink = async (email: string) =>
    axios
        .post(`${API_URL}/password_reset`, { email })
        .then(({ data }) => {
            if (data && data.result && data.result === 'Please check your E-mail') {
                return data.result
            }
            throw new Error()
        })
        .catch(() => ({ hasError: true }))

export {
    handleLogging, logOut, regenerateToken, requestCreateNewUser, requestEmailResetLink, requestIsUserRegistered, requestLogIn,
    requestUpdateUserData, requestUserInfo
}

